import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { config as appConfig } from "@/config";
import { ApiResponse, ApiError } from "@/types";
import { getStoredOrgContext, storage } from "@/utils";
import { getValidatedCurrentWorkflowOrganization } from "@/utils/workflow-utils";
import { isReAuthModalOpen } from "@/stores/auth/reauth-store";
import { handle401WithSilentRefresh } from "./authRefresh";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import type { Organization } from "@/stores/organization/organization-store";
import { CsrfDataSchema } from "@/schemas/auth";
import { getCurrentAuthTokens } from "@/services/api/auth-session";
import {
  authDebug,
  authDebugWarn,
  describeAuthError,
} from "@/stores/auth/auth-diagnostics";

export interface ApiRetryPolicy {
  onRateLimit?: "retry" | "skip";
}

// Extend axios request config to include custom metadata and retry policy
declare module "axios" {
  export interface AxiosRequestConfig {
    retryPolicy?: ApiRetryPolicy;
    skipAuthRecovery?: boolean;
    suppressErrorLog?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
    retryPolicy?: ApiRetryPolicy;
    _retry?: boolean;
    _csrfRetry?: boolean;
    _rateLimitRetry?: number; // Track 429 retry attempts
    skipAuthRecovery?: boolean;
    suppressErrorLog?: boolean;
  }
}

// Constants for rate limit handling
const MAX_RATE_LIMIT_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 1000;
const PUBLIC_AUTH_MUTATION_PATHS = new Set([
  "/auth/forgot-password",
  "/auth/login",
  "/auth/register",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/resend-verification",
]);
const AUTH_RATE_LIMIT_NO_RETRY_PATHS = new Set([
  ...PUBLIC_AUTH_MUTATION_PATHS,
  "/auth/refresh",
]);
const PUBLIC_AUTH_RESPONSE_PATHS = new Set([
  "/auth/forgot-password",
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/resend-verification",
]);

const getRequestPathname = (requestUrl: string | undefined): string => {
  if (!requestUrl) {
    return "";
  }

  try {
    const parsed = new URL(requestUrl, appConfig.api.baseUrl);
    const authPathIndex = parsed.pathname.indexOf("/auth/");
    return authPathIndex >= 0
      ? parsed.pathname.slice(authPathIndex)
      : parsed.pathname;
  } catch {
    const [path] = requestUrl.split("?");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const authPathIndex = normalizedPath.indexOf("/auth/");
    return authPathIndex >= 0
      ? normalizedPath.slice(authPathIndex)
      : normalizedPath;
  }
};

const isAuthRateLimitNoRetryPath = (
  request: InternalAxiosRequestConfig | undefined,
): boolean => {
  const method = request?.method?.toLowerCase();
  return (
    method === "post" &&
    AUTH_RATE_LIMIT_NO_RETRY_PATHS.has(getRequestPathname(request?.url))
  );
};

const isPublicAuthResponse = (
  request: InternalAxiosRequestConfig | undefined,
): boolean => PUBLIC_AUTH_RESPONSE_PATHS.has(getRequestPathname(request?.url));

const isPublicAuthMutation = (
  request: InternalAxiosRequestConfig | undefined,
): boolean =>
  request?.method?.toLowerCase() === "post" &&
  PUBLIC_AUTH_MUTATION_PATHS.has(getRequestPathname(request?.url));

const shouldRetryRateLimit = (
  request: InternalAxiosRequestConfig | undefined,
): boolean =>
  !isPublicAuthRateLimitMutation(request) &&
  request?.retryPolicy?.onRateLimit !== "skip";

const getStringProperty = (
  source: unknown,
  property: string,
): string | undefined => {
  if (source === null || typeof source !== "object") {
    return undefined;
  }

  const value = Reflect.get(source, property);
  return typeof value === "string" ? value : undefined;
};

const getUnknownProperty = (source: unknown, property: string): unknown => {
  if (source === null || typeof source !== "object") {
    return undefined;
  }

  return Reflect.get(source, property);
};

const toApiError = (error: unknown): ApiError => {
  const responseData = axios.isAxiosError<unknown>(error)
    ? error.response?.data
    : undefined;

  const apiError: ApiError = {
    code: getStringProperty(responseData, "code") || "UNKNOWN_ERROR",
    message:
      getStringProperty(responseData, "message") ||
      getStringProperty(responseData, "error") ||
      (error instanceof Error ? error.message : undefined) ||
      "An unexpected error occurred",
    details: getUnknownProperty(responseData, "details"),
    status: axios.isAxiosError<unknown>(error)
      ? error.response?.status
      : undefined,
  };

  if (appConfig.app.debug && error instanceof Error) {
    apiError.stack = error.stack;
  }

  return apiError;
};

// Silent-refresh coordination and the re-auth modal fallback live in
// authRefresh.ts so that every axios client (main, agent, data-service,
// workflow, knowledge) shares one refresh-in-progress flag and one canonical
// 401 handler. Before extraction, each client had its own (or no) policy and
// a revoked-jti 401 would silently fail in one client while another worked.
// See authRefresh.ts header for the full rationale.

export const getVisibleOrganizationsForHeaderValidation = async (): Promise<
  Organization[]
> => {
  const { useOrganizationStore } = await import(
    "@/stores/organization/organization-store"
  );
  const { organizations, allOrganizations, platformAdminEntitlement } =
    useOrganizationStore.getState();

  return platformAdminEntitlement
    ? [...organizations, ...allOrganizations]
    : organizations;
};

export const getValidatedOrganizationBySlug = async (
  orgSlug: string,
): Promise<Organization | null> => {
  const visibleOrganizations = await getVisibleOrganizationsForHeaderValidation();
  return visibleOrganizations.find((org) => org.slug === orgSlug) ?? null;
};

export const getValidatedOrganizationById = async (
  orgId: string,
): Promise<Organization | null> => {
  const visibleOrganizations = await getVisibleOrganizationsForHeaderValidation();
  return visibleOrganizations.find((org) => org._id === orgId) ?? null;
};

export const getHeaderValue = (
  headers: InternalAxiosRequestConfig["headers"],
  name: string,
): string | null => {
  const getHeader = Reflect.get(headers, "get");
  const value =
    typeof getHeader === "function"
      ? getHeader.call(headers, name)
      : Object.entries(headers).find(
          ([headerName]) => headerName.toLowerCase() === name.toLowerCase(),
        )?.[1];

  return typeof value === "string" && value.trim() ? value.trim() : null;
};

export const hasHeader = (
  headers: InternalAxiosRequestConfig["headers"],
  name: string,
): boolean => getHeaderValue(headers, name) !== null;

export const setHeader = (
  headers: InternalAxiosRequestConfig["headers"],
  name: string,
  value: string,
): void => {
  if (typeof headers.set === "function") {
    headers.set(name, value);
    return;
  }

  Reflect.set(headers, name, value);
};

export const deleteHeader = (
  headers: InternalAxiosRequestConfig["headers"],
  name: string,
): void => {
  const deleteHeaderValue = Reflect.get(headers, "delete");
  if (typeof deleteHeaderValue === "function") {
    deleteHeaderValue.call(headers, name);
    return;
  }

  const matchingHeaderName = Object.keys(headers).find(
    (headerName) => headerName.toLowerCase() === name.toLowerCase(),
  );
  if (matchingHeaderName) {
    Reflect.deleteProperty(headers, matchingHeaderName);
  }

  for (const headerName of Object.keys(headers)) {
    if (headerName.toLowerCase() === name.toLowerCase()) {
      Reflect.deleteProperty(headers, headerName);
    }
  }
};

const RESERVED_ORGANIZATION_HEADER_NAMES = [
  "X-Organization-Context",
  "X-Organization-Id",
  "x-tenant-id",
] as const;

export const clearOrganizationHeaders = (
  headers: InternalAxiosRequestConfig["headers"],
): void => {
  for (const headerName of RESERVED_ORGANIZATION_HEADER_NAMES) {
    deleteHeader(headers, headerName);
  }
};

export const omitReservedOrganizationHeaders = (
  headers: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers).filter(
      ([headerName]) =>
        !RESERVED_ORGANIZATION_HEADER_NAMES.some(
          (reservedHeaderName) =>
            reservedHeaderName.toLowerCase() === headerName.toLowerCase(),
        ),
    ),
  );

export const hasExplicitOrganizationHeaders = (
  headers: InternalAxiosRequestConfig["headers"],
): boolean =>
  hasHeader(headers, "X-Organization-Id") ||
  hasHeader(headers, "X-Organization-Context");

export const applyCanonicalOrganizationHeaders = async (
  headers: InternalAxiosRequestConfig["headers"],
  options?: {
    organizationId?: string | null;
    organizationSlug?: string | null;
  },
): Promise<void> => {
  const validatedOrganization = options
    ? await getValidatedExplicitOrganization(options)
    : await getValidatedStoredOrganization();

  clearOrganizationHeaders(headers);
  if (!validatedOrganization) {
    return;
  }

  setValidatedOrganizationHeaders(headers, validatedOrganization);
};

export const setValidatedOrganizationHeaders = (
  headers: InternalAxiosRequestConfig["headers"],
  organization: Organization,
): void => {
  clearOrganizationHeaders(headers);
  setHeader(headers, "X-Organization-Context", organization.slug);
  setHeader(headers, "X-Organization-Id", organization._id);
};

const normalizeExplicitOrganizationOption = (
  value: string | null | undefined,
): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export const getValidatedExplicitOrganization = async (options: {
  organizationId?: string | null;
  organizationSlug?: string | null;
}): Promise<Organization | null> => {
  const explicitOrgContext = normalizeExplicitOrganizationOption(
    options.organizationSlug,
  );
  const explicitOrgId = normalizeExplicitOrganizationOption(options.organizationId);

  if (!explicitOrgContext && !explicitOrgId) {
    return null;
  }

  try {
    const organizationFromContext = explicitOrgContext
      ? await getValidatedOrganizationBySlug(explicitOrgContext)
      : null;
    const organizationFromId = explicitOrgId
      ? await getValidatedOrganizationById(explicitOrgId)
      : null;
    const validatedOrganization = organizationFromContext ?? organizationFromId;

    if (
      !validatedOrganization ||
      (explicitOrgContext &&
        validatedOrganization.slug !== explicitOrgContext) ||
      (explicitOrgId && validatedOrganization._id !== explicitOrgId)
    ) {
      return null;
    }

    return validatedOrganization;
  } catch (error) {
    authDebugWarn(
      "api.org-context.explicit-organization-failed",
      describeAuthError(error),
    );
    return null;
  }
};

export const applyValidatedExplicitOrganizationHeaders = async (
  headers: InternalAxiosRequestConfig["headers"],
): Promise<void> => {
  await applyCanonicalOrganizationHeaders(headers, {
    organizationId: getHeaderValue(headers, "X-Organization-Id"),
    organizationSlug: getHeaderValue(headers, "X-Organization-Context"),
  });
};

export const getValidatedOrganizationContextHeaderValue = async (): Promise<
  string | null
> => {
  const orgContext = getStoredOrgContext();
  if (orgContext?.type !== "organization" || !orgContext.orgSlug) {
    return null;
  }

  try {
    const { useOrganizationStore } = await import(
      "@/stores/organization/organization-store"
    );
    const store = useOrganizationStore.getState();
    const {
      organizations,
      allOrganizations,
      platformAdminEntitlement,
      currentContext,
      clearCurrentContext,
    } = store;
    const memberOrg = organizations.some(
      (org) => org.slug === orgContext.orgSlug,
    );
    const platformVisibleOrg =
      platformAdminEntitlement &&
      allOrganizations.some((org) => org.slug === orgContext.orgSlug);

    if (memberOrg || platformVisibleOrg) {
      return orgContext.orgSlug;
    }

    if (platformAdminEntitlement && allOrganizations.length === 0) {
      return null;
    }

    if (
      currentContext?.type === "organization" &&
      currentContext.orgSlug === orgContext.orgSlug
    ) {
      clearCurrentContext();
    } else {
      storage.remove("terramatrix:org-context");
      if (typeof window !== "undefined") {
        window.sessionStorage?.removeItem("terramatrix:org-context");
      }
    }
    return null;
  } catch (error) {
    authDebugWarn(
      "api.org-context.validation-failed",
      describeAuthError(error),
    );
    return null;
  }
};

export const getValidatedStoredOrganization = async (): Promise<
  Organization | null
> => {
  const currentOrganization =
    typeof getValidatedCurrentWorkflowOrganization === "function"
      ? getValidatedCurrentWorkflowOrganization()
      : null;
  if (currentOrganization) {
    return currentOrganization;
  }

  const orgContextSlug = await getValidatedOrganizationContextHeaderValue();
  if (!orgContextSlug) {
    return null;
  }

  return getValidatedOrganizationBySlug(orgContextSlug);
};

export const applyValidatedStoredOrganizationHeaders = async (
  headers: InternalAxiosRequestConfig["headers"],
): Promise<void> => {
  await applyCanonicalOrganizationHeaders(headers);
};

export const getValidatedStoredOrganizationHeaderValues = async (): Promise<
  Record<string, string>
> => {
  const organization = await getValidatedStoredOrganization();
  if (!organization) {
    return {};
  }

  return {
    "X-Organization-Context": organization.slug,
    "X-Organization-Id": organization._id,
  };
};

export const getValidatedExplicitOrganizationHeaderValues = async (options: {
  organizationId?: string | null;
  organizationSlug?: string | null;
}): Promise<Record<string, string>> => {
  const validatedOrganization = await getValidatedExplicitOrganization(options);
  if (!validatedOrganization) {
    return {};
  }

  return {
    "X-Organization-Context": validatedOrganization.slug,
    "X-Organization-Id": validatedOrganization._id,
  };
};

// fallToReAuthModal and handle401WithSilentRefresh now live in authRefresh.ts
// so every client uses the same fallback path. Import them where needed.

// Create axios instance with default configuration
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: appConfig.api.baseUrl,
    timeout: appConfig.api.timeout,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor for authentication and CSRF protection
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Note: We no longer block requests when re-auth is needed
      // Instead, 401 responses will be caught and queued for retry after re-auth

      // Add auth token if available
      const tokens = getCurrentAuthTokens();
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      const hasPerRequestOrgOverride =
        hasExplicitOrganizationHeaders(config.headers);

      if (hasPerRequestOrgOverride) {
        await applyValidatedExplicitOrganizationHeaders(config.headers);
      } else {
        // Add validated organization context headers for multi-org support.
        await applyValidatedStoredOrganizationHeaders(config.headers);

        // Viewer org override for cross-org data access (scoped to viewer)
        const viewerOrgId = useViewerStore.getState().viewerOrgId;
        if (viewerOrgId) {
          const viewerOrganization =
            await getValidatedOrganizationById(viewerOrgId);
          if (viewerOrganization) {
            setValidatedOrganizationHeaders(config.headers, viewerOrganization);
          } else {
            authDebugWarn("api.org-context.viewer-override-rejected", {
              viewerOrgId,
            });
          }
        }
      }

      // SECURITY: Add CSRF token for state-changing requests
      const method = config.method?.toLowerCase();
      if (method && ["post", "put", "patch", "delete"].includes(method)) {
        // Skip CSRF token for specific endpoints
        const skipCSRF =
          config.headers?.["X-Skip-CSRF"] ||
          config.url?.includes("/auth/refresh");

        if (!skipCSRF) {
          const csrfData = sessionStorage.getItem("terramatrix_csrf_token");
          if (csrfData) {
            try {
              const raw: unknown = JSON.parse(csrfData);
              const result = CsrfDataSchema.safeParse(raw);
              if (result.success) {
                const tokenAge = Date.now() - result.data.timestamp;
                const isExpired = tokenAge > 14 * 60 * 1000; // 14 minutes

                if (!isExpired) {
                  config.headers["X-CSRF-Token"] = result.data.token;
                }
              }
            } catch {
              console.warn("[client] CSRF token parse failed");
            }
          }
        }
      }

      // Add request timestamp for debugging
      config.metadata = { startTime: Date.now() };

      return config;
    },
    (error) => {
      console.error(
        "❌ Request interceptor error:",
        describeAuthError(error),
      );
      return Promise.reject(error);
    },
  );

  // Note: Token refresh and request queuing are now handled by the ReAuth store
  // This keeps the app state intact while prompting the user to re-authenticate

  // Response interceptor for error handling and re-auth triggering
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        isPublicAuthMutation(originalRequest)
      ) {
        authDebugWarn("api.auth.public-mutation", {
          requestUrl: originalRequest?.url || "",
          status: error.response?.status,
        });
        return Promise.reject(toApiError(error));
      }

      // SECURITY: Handle CSRF token validation failures
      if (
        error.response?.status === 403 &&
        error.response?.data?.code === "CSRF_TOKEN_INVALID" &&
        !isPublicAuthResponse(originalRequest) &&
        !originalRequest._csrfRetry
      ) {
        originalRequest._csrfRetry = true;

        try {
          // Clear invalid CSRF token
          sessionStorage.removeItem("terramatrix_csrf_token");

          // Fetch new CSRF token by making a GET request
          const csrfResponse = await client.get("/csrf-token", {
            headers: { "X-Skip-CSRF": "true" },
          });

          const newToken = csrfResponse.headers["x-csrf-token"];
          if (newToken) {
            // Store new token
            sessionStorage.setItem(
              "terramatrix_csrf_token",
              JSON.stringify({
                token: newToken,
                timestamp: Date.now(),
              }),
            );

            // Retry original request with new CSRF token
            originalRequest.headers["X-CSRF-Token"] = newToken;
            return client(originalRequest);
          }
        } catch (csrfError) {
          console.error(
            "❌ CSRF token refresh failed:",
            describeAuthError(csrfError),
          );
        }
      }

      // BUDGET_EXCEEDED — distinct from generic rate limiting. Do NOT retry; surface
      // a banner so the user knows their org has hit its LLM cap.
      if (
        error.response?.status === 429 &&
        error.response?.data?.error === "BUDGET_EXCEEDED"
      ) {
        try {
          const detail = {
            currentUsageUsd: error.response?.data?.currentUsageUsd,
            limitUsd: error.response?.data?.limitUsd,
            remainingUsd: error.response?.data?.remainingUsd,
            periodEndsAt: error.response?.data?.periodEndsAt,
            message: error.response?.data?.message,
          };
          window.dispatchEvent(
            new CustomEvent("llm-budget-exceeded", { detail }),
          );
        } catch {
          // window may not exist in SSR / tests
        }
        return Promise.reject(error);
      }

      // Handle 429 errors (rate limiting) with exponential backoff. Public auth
      // mutations are not retried because the server owns throttling semantics.
      if (error.response?.status === 429 && shouldRetryRateLimit(originalRequest)) {
        const retryCount = originalRequest._rateLimitRetry || 0;

        if (retryCount < MAX_RATE_LIMIT_RETRIES) {
          originalRequest._rateLimitRetry = retryCount + 1;

          // Get retry delay from Retry-After header or use exponential backoff
          const retryAfterHeader = error.response.headers?.["retry-after"];
          let delayMs: number;

          if (retryAfterHeader) {
            // Retry-After can be seconds or HTTP date
            const retryAfterSeconds = parseInt(retryAfterHeader, 10);
            delayMs = isNaN(retryAfterSeconds)
              ? DEFAULT_RETRY_DELAY_MS
              : retryAfterSeconds * 1000;
          } else {
            // Exponential backoff: 1s, 2s, 4s
            delayMs = DEFAULT_RETRY_DELAY_MS * Math.pow(2, retryCount);
          }

          // Cap delay at 10 seconds
          delayMs = Math.min(delayMs, 10000);

          console.warn(
            `⏳ Rate limited (429). Retrying in ${delayMs}ms (attempt ${retryCount + 1}/${MAX_RATE_LIMIT_RETRIES})`,
          );

          // Wait and retry
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          return client(originalRequest);
        }

        // Max retries exceeded - let the error propagate
        console.error(
          `❌ Rate limit retry exhausted after ${MAX_RATE_LIMIT_RETRIES} attempts`,
        );
      }

      // Handle 401 errors (unauthorized) — delegate to the shared handler
      // so every axios client (main, agent, data-service, workflow,
      // knowledge) follows the same silent-refresh-then-modal policy.
      if (error.response?.status === 401 && !originalRequest._retry) {
        const requestUrl = originalRequest.url || "";

        // Don't intercept public auth attempts - let them fail normally
        if (isPublicAuthResponse(originalRequest)) {
          authDebugWarn("api.401.auth-endpoint", {
            requestUrl,
            status: error.response?.status,
          });
          return Promise.reject(toApiError(error));
        }

        // Only handle 401s if we HAD a token that failed
        const hadToken = getCurrentAuthTokens();
        authDebugWarn("api.401", {
          requestUrl,
          hadToken: Boolean(hadToken?.accessToken),
          reauthOpen: isReAuthModalOpen(),
        });

        if (hadToken?.accessToken) {
          originalRequest._retry = true;
          try {
            return await handle401WithSilentRefresh(error, client);
          } catch (handlerError) {
            // Surface as a normalized ApiError consistent with other errors.
            return Promise.reject(toApiError(handlerError));
          }
        }

        // For requests without a token, just reject without opening modal
        console.debug(
          `401 without existing token: ${requestUrl} - expected, not an error`,
        );
      }

      // Handle other errors
      const apiError = toApiError(error);

      if (appConfig.app.debug) {
        console.error("🚨 API Error:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          error: apiError,
        });
        apiError.stack = error.stack;
      }

      return Promise.reject(apiError);
    },
  );

  return client;
};

// Create the API client instance
export const apiClient = createApiClient();

// Type-safe API client wrapper
export class ApiClient {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  // GET request
  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  // POST request
  async post<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  // PUT request
  async put<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  // PATCH request
  async patch<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  // DELETE request
  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url, config);
    return response.data;
  }

  // Upload file
  async upload<T = unknown>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
    config?: AxiosRequestConfig,
    fieldName = "file",
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await this.client.post(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Download file
  async download(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const response = await this.client.get(url, {
      ...config,
      responseType: "blob",
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get request config for manual use
  getRequestConfig(config?: AxiosRequestConfig): AxiosRequestConfig {
    const headers: AxiosRequestConfig["headers"] = {
      ...this.client.defaults.headers.common,
      ...config?.headers,
    };

    return {
      ...config,
      baseURL: this.client.defaults.baseURL,
      timeout: this.client.defaults.timeout,
      headers,
    };
  }
}

// Export the configured API client
export const api = new ApiClient(apiClient);

// Utility functions for common patterns
export const createApiHook = <TData = unknown>(
  queryFn: () => Promise<ApiResponse<TData>>,
) => {
  return queryFn;
};

export const createMutationFn = <TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
) => {
  return mutationFn;
};

// Request cancellation utility
export const createCancelToken = () => {
  const source = axios.CancelToken.source();
  return {
    token: source.token,
    cancel: source.cancel,
  };
};

// Type guards for API responses
export const isApiError = (error: unknown): error is ApiError => {
  if (error === null || typeof error !== "object") {
    return false;
  }

  const code = Reflect.get(error, "code");
  const message = Reflect.get(error, "message");

  return (
    "code" in error &&
    "message" in error &&
    typeof code === "string" &&
    typeof message === "string"
  );
};

export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (isApiError(error) && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (isApiError(error)) {
    return error.status;
  }

  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
};

export const isApiResponse = <T>(
  response: unknown,
): response is ApiResponse<T> => {
  if (response === null || typeof response !== "object") {
    return false;
  }

  const success = Reflect.get(response, "success");

  return "success" in response && typeof success === "boolean";
};
